import { createApp } from 'vue'
import { createPinia } from 'pinia'
import 'vuetify/dist/vuetify.min.css'

import App from './App.vue'
import router from './router'
import vuetify from './plugins/vuetify'

import * as Sentry from '@sentry/vue'
import { UserManagementApp } from '@haskoning/vue-user-management'
import { AuthenticationManagement } from '@haskoning/vue-authentication-middleware'
import { ComponentLibrary, useEventsBus } from '@haskoning/vue-component-library'
import {
  setSpeckleConfig,
  useSpeckleStore,
  VueSpecklePlugin
} from '@haskoning/vue-speckle'

import HeaderImage from '/header5.jpeg'
import HeaderImageLazy from '/header5_lazy.jpg'
import apiUrls from '@/api/config/apiUrls'

import { useDjangoAuthStore } from '@/stores/authDjango'
import { useApi } from '@/api/config/useApi'
import { useAuthorizationStore } from '@/stores/authorization'
import { useSentryFeedback } from '@/composables/useSentryFeedback'

const pinia = createPinia()

const { api } = useApi()
const { bus } = useEventsBus()

const authStore = useDjangoAuthStore(pinia)

pinia.use(({ store }) => {
  store.$api = api
  store.$apiUrls = apiUrls
})

const app = createApp(App).use(router).use(vuetify).use(pinia)

app
  .use(AuthenticationManagement, {
    appTitle: 'MOOR²',
    headerImage: HeaderImage,
    headerImageLazy: HeaderImageLazy,
    feathers: api,
    authStore: authStore,
    showUsernameAndPasswordLogin: false,
    apiBaseUrl: import.meta.env.VITE_APP_API_BASE_URL,
    showForgotPassword: true,
    signInWithAccount: false
  })
  .provide('useAuthStore', authStore)
  .provide('CustomLogInButton', null)

const speckleConfig = {
  serverUrl: import.meta.env.VITE_APP_SERVER_URL,
  workspaceId: import.meta.env.VITE_APP_SPECKLE_WORKSPACE_ID,
  appId: import.meta.env.VITE_APP_SPECKLE_ID,
  appSecret: import.meta.env.VITE_APP_SPECKLE_SECRET,
  appName: import.meta.env.VITE_APP_SPECKLE_NAME
}
setSpeckleConfig(
  speckleConfig.serverUrl,
  speckleConfig.workspaceId,
  speckleConfig.appId,
  speckleConfig.appSecret,
  speckleConfig.appName
)

app.use(VueSpecklePlugin, {
  componentsAutoSelectData: true,
  speckleStore: () => useSpeckleStore({ autoSelectData: false })
})

app.use(ComponentLibrary, { externalBus: bus })

app.use(UserManagementApp, {
  authorizationStore: useAuthorizationStore(),
  authStore: authStore
})


Sentry.init({
  app,
  dsn: import.meta.env.VITE_APP_SENTRY_DSN,
  environment: import.meta.env.MODE,
  tracePropagationTargets: ['*'],
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration(),
    Sentry.feedbackIntegration({
      triggerLabel: 'Feedback',
      formTitle: 'Send Feedback',
      submitButtonLabel: 'Send Feedback',
      cancelButtonLabel: 'Cancel',
      messagePlaceholder: 'What would you like to tell us?',
      successMessageText: 'Thank you for your feedback!',
      colorScheme: 'light',
      showName: true,
      showEmail: true,
      enableScreenshot: true,
      autoInject: false
    })
  ],
  // Performance Monitoring
  tracesSampleRate: 0.1, // Capture 10% of the transactions
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in cloud and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
})

app.mount('#app')
router.isReady().then(() => {
  useSentryFeedback()
})

export default app
